import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { Button, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action, ApiError } from '../../lib/api.ts';
import { dateTime } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { roleLabel, type RoleInfo } from './Staff.tsx';

type Protocol = 'oidc' | 'saml';

interface Settings {
  enabled: boolean;
  protocol: Protocol;
  label: string;
  issuer: string;
  client_id: string;
  has_secret: boolean;
  domains: string[];
  sso_only: boolean;
  trust_mfa: boolean;
  create_role: string | null;
  saml_entry_point: string;
  saml_idp_entity: string;
  saml_idp_cert: string;
  saml_cert_info: { subject: string; valid_to: string } | null;
  updated_at: string | null;
}
interface Data {
  settings: Settings;
  roles: Pick<RoleInfo, 'name' | 'label'>[];
  people: { with_email: number; linked: number; total: number };
  can_seal: boolean;
  callback_path: string;
  saml_entity_path: string;
  saml_acs_path: string;
}

const CERT_BODY = /^[A-Za-z0-9+/=]{100,}$/;
const certLooksRight = (c: string) => CERT_BODY.test(c.replace(/-----(BEGIN|END) CERTIFICATE-----/g, '').replace(/\s+/g, ''));

/** Where each well-known provider's issuer lives; the business fills in its own part. */
const PROVIDERS = [
  { key: 'microsoft', name: 'Microsoft Entra ID', issuer: 'https://login.microsoftonline.com/<tenant-id>/v2.0' },
  { key: 'google', name: 'Google Workspace', issuer: 'https://accounts.google.com' },
  { key: 'okta', name: 'Okta', issuer: 'https://<your-company>.okta.com' },
  { key: 'other', name: '', issuer: '' },
] as const;

const DOMAIN = /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const splitDomains = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
    .filter(Boolean);

/**
 * Manage → Single sign-on: people sign in with the account their company already runs
 * (Microsoft, Google, Okta or any OpenID Connect provider) instead of a PIN or password.
 */
export function SingleSignOn() {
  const { data, reload } = useAction<Data>('company.sso');
  return (
    <ManageLayout title={t('sso.title')} subtitle={t('sso.subtitle')}>
      {data ? <SsoForm data={data} onSaved={reload} /> : <Skeleton h={420} r="var(--radius-lg)" />}
    </ManageLayout>
  );
}

function SsoForm({ data, onSaved }: { data: Data; onSaved: () => Promise<void> }) {
  const { me } = useSession();
  const { toast } = useUi();
  const { busy, run } = useSave();
  const s = data.settings;
  const [enabled, setEnabled] = useState(s.enabled);
  const [protocol, setProtocol] = useState<Protocol>(s.protocol);
  const [entryPoint, setEntryPoint] = useState(s.saml_entry_point);
  const [idpEntity, setIdpEntity] = useState(s.saml_idp_entity);
  const [cert, setCert] = useState(s.saml_idp_cert);
  const [label, setLabel] = useState(s.label);
  const [issuer, setIssuer] = useState(s.issuer);
  const [clientId, setClientId] = useState(s.client_id);
  const [secret, setSecret] = useState('');
  const [domains, setDomains] = useState(s.domains.join(', '));
  const [createRole, setCreateRole] = useState(s.create_role ?? '');
  const [trustMfa, setTrustMfa] = useState(s.trust_mfa);
  const [ssoOnly, setSsoOnly] = useState(s.sso_only);
  const [tried, setTried] = useState(false);
  const [check, setCheck] = useState<{ busy?: boolean; ok?: string; error?: string } | null>(null);
  const [serverField, setServerField] = useState<{ field: string; message: string } | null>(null);
  useEffect(() => setCheck(null), [issuer]);

  const callback = `${window.location.origin}${data.callback_path}`;
  const entityId = `${window.location.origin}${data.saml_entity_path}`;
  const acs = `${window.location.origin}${data.saml_acs_path}`;
  const saml = protocol === 'saml';
  const list = splitDomains(domains);
  const badDomain = list.find((d) => !DOMAIN.test(d));
  const errors: Record<string, string | null> = saml
    ? {
        saml_entry_point: enabled && !/^https:\/\/[^\s/<>]+/.test(entryPoint.trim()) ? t('sso.e_entry') : null,
        saml_idp_cert: cert.trim() ? (certLooksRight(cert) ? null : t('sso.e_cert')) : enabled ? t('sso.e_cert_needed') : null,
        domains: badDomain ? t('sso.e_domain', { domain: badDomain }) : createRole && !list.length ? t('sso.e_domains_needed') : null,
      }
    : {
    issuer: enabled && !/^https:\/\/[^\s/<>]+/.test(issuer.trim()) ? t('sso.e_issuer') : enabled && /[<>]/.test(issuer) ? t('sso.e_issuer_fill') : null,
    client_id: enabled && !clientId.trim() ? t('sso.e_client_id') : null,
    client_secret: enabled && !secret.trim() && !s.has_secret ? t('sso.e_secret') : null,
    domains: badDomain ? t('sso.e_domain', { domain: badDomain }) : createRole && !list.length ? t('sso.e_domains_needed') : null,
      };
  const shown = (f: string) => (tried ? errors[f] : null) ?? (serverField?.field === f ? serverField.message : null);

  const verify = async () => {
    setCheck({ busy: true });
    try {
      const r = await action<{ issuer: string; sign_in_at: string }>('company.test_sso', { issuer: issuer.trim() });
      setCheck({ ok: t('sso.check_ok', { host: r.sign_in_at }) });
    } catch (err) {
      setCheck({ error: err instanceof ApiError ? err.message : t('error.generic') });
    }
  };

  const save = async () => {
    setTried(true);
    setServerField(null);
    if (Object.values(errors).some(Boolean)) return;
    let field: { field: string; message: string } | null = null;
    const ok = await run(async () => {
      try {
        await action('company.save_sso', {
          enabled,
          protocol,
          saml_entry_point: entryPoint.trim(),
          saml_idp_entity: idpEntity.trim(),
          saml_idp_cert: cert.trim(),
          label: label.trim(),
          issuer: issuer.trim(),
          client_id: clientId.trim(),
          ...(secret.trim() ? { client_secret: secret.trim() } : {}),
          domains: list,
          sso_only: enabled && ssoOnly,
          trust_mfa: trustMfa,
          create_role: createRole || null,
        });
      } catch (err) {
        const f = err instanceof ApiError ? (err.details?.field as string | undefined) : undefined;
        if (f && err instanceof ApiError) field = { field: f, message: err.message };
        throw err;
      }
    }, t(enabled ? 'sso.saved_on' : 'sso.saved_off'));
    setServerField(field);
    if (ok) {
      setSecret('');
      setTried(false);
      await onSaved();
    }
  };

  const copy = (text = callback) => void navigator.clipboard?.writeText(text).then(() => toast(t('sso.copied')));
  const pick = (p: (typeof PROVIDERS)[number]) => {
    if (p.issuer) setIssuer(p.issuer);
    if (p.name && !label.trim()) setLabel(p.name);
  };
  const live = s.enabled;

  return (
    <div className="sso-page">
      {!me?.mfa ? (
        <p className="form-note" role="status">
          <Icon name="shieldCheck" size={16} /> {t('sso.needs_mfa')} <Link to="/security">{t('sso.needs_mfa_link')}</Link>
        </p>
      ) : null}

      <section className="card sso-status" aria-labelledby="sso-now">
        <div className="card-head">
          <h2 id="sso-now">
            <Icon name="key" size={18} /> {t('sso.status_title')}
          </h2>
          {live ? <StatusBadge tone="ok" icon="check" label={s.sso_only ? t('sso.on_only') : t('sso.on')} /> : <StatusBadge tone="neutral" icon="pause" label={t('sso.off')} />}
        </div>
        <p className="muted">{live ? t('sso.status_on', { label: s.label || t('sso.default_label') }) : t('sso.status_off')}</p>
        <dl className="sso-stats">
          <div>
            <dt>{t('sso.stat_emails')}</dt>
            <dd className="tabular">
              {data.people.with_email} / {data.people.total}
            </dd>
          </div>
          <div>
            <dt>{t('sso.stat_linked')}</dt>
            <dd className="tabular">{data.people.linked}</dd>
          </div>
          {s.updated_at ? (
            <div>
              <dt>{t('sso.stat_changed')}</dt>
              <dd>{dateTime(s.updated_at)}</dd>
            </div>
          ) : null}
        </dl>
        <p className="small">
          <Link to="/manage/staff">{t('sso.people_link')}</Link>
        </p>
      </section>

      <section className="card" aria-labelledby="sso-provider">
        <div className="card-head">
          <h2 id="sso-provider">
            <span className="sso-step">1</span> {t('sso.provider_title')}
          </h2>
        </div>
        <div className="segmented subtle sso-protocol" role="radiogroup" aria-label={t('sso.protocol')}>
          {(['oidc', 'saml'] as const).map((p) => (
            <button key={p} type="button" role="radio" aria-checked={protocol === p} className={protocol === p ? 'active' : ''} onClick={() => setProtocol(p)}>
              {t(p === 'oidc' ? 'sso.protocol_oidc' : 'sso.protocol_saml')}
            </button>
          ))}
        </div>
        <p className="muted">{t(saml ? 'sso.saml_body' : 'sso.provider_body')}</p>
        {saml ? (
          <>
            <div className="sso-inline">
              <Field label={t('sso.saml_entity')} hint={t('sso.saml_entity_hint')}>
                <input className="input tabular" readOnly value={entityId} onFocus={(e) => e.currentTarget.select()} />
              </Field>
              <Button icon="copy" onClick={() => copy(entityId)}>
                {t('sso.copy')}
              </Button>
            </div>
            <div className="sso-inline">
              <Field label={t('sso.saml_acs')} hint={t('sso.saml_acs_hint')}>
                <input className="input tabular" readOnly value={acs} onFocus={(e) => e.currentTarget.select()} />
              </Field>
              <Button icon="copy" onClick={() => copy(acs)}>
                {t('sso.copy')}
              </Button>
            </div>
            <Field label={t('sso.saml_entry')} hint={t('sso.saml_entry_hint')} error={shown('saml_entry_point')}>
              <input
                className="input"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={500}
                placeholder="https://"
                value={entryPoint}
                onChange={(e) => setEntryPoint(e.target.value.trim())}
              />
            </Field>
            <Field label={t('sso.saml_idp_entity')} hint={t('sso.saml_idp_entity_hint')} error={shown('saml_idp_entity')}>
              <input className="input" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={500} value={idpEntity} onChange={(e) => setIdpEntity(e.target.value.trim())} />
            </Field>
            <Field
              label={t('sso.saml_cert')}
              hint={
                s.saml_cert_info && cert === s.saml_idp_cert
                  ? t('sso.saml_cert_kept', { subject: s.saml_cert_info.subject, date: dateTime(s.saml_cert_info.valid_to) })
                  : t('sso.saml_cert_hint')
              }
              error={shown('saml_idp_cert')}
            >
              <textarea
                className="input mono sso-cert"
                rows={5}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="-----BEGIN CERTIFICATE-----"
                value={cert}
                onChange={(e) => setCert(e.target.value)}
              />
            </Field>
            <Field label={t('sso.label')} hint={t('sso.label_hint', { label: label.trim() || t('sso.default_label') })}>
              <input className="input sso-label" maxLength={40} placeholder="ADFS" value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
          </>
        ) : (
          <>
        <div className="chips" role="group" aria-label={t('sso.provider_pick')}>
          {PROVIDERS.map((p) => (
            <button key={p.key} type="button" className={`chip${p.issuer && issuer === p.issuer ? ' active' : ''}`} onClick={() => pick(p)}>
              {p.name || t('sso.other_provider')}
            </button>
          ))}
        </div>
        <div className="sso-inline">
          <Field label={t('sso.issuer')} hint={check?.ok ? undefined : t('sso.issuer_hint')} ok={check?.ok ?? null} error={check?.error ?? shown('issuer')}>
            <input
              className="input"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={300}
              placeholder="https://"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value.trim())}
            />
          </Field>
          <Button icon="search" busy={check?.busy} disabled={!/^https:\/\/[^\s/<>]+/.test(issuer) || /[<>]/.test(issuer)} onClick={() => void verify()}>
            {t('sso.check')}
          </Button>
        </div>
        <div className="form-grid">
          <Field label={t('sso.client_id')} error={shown('client_id')}>
            <input className="input" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={300} value={clientId} onChange={(e) => setClientId(e.target.value)} />
          </Field>
          <Field label={t('sso.client_secret')} hint={s.has_secret ? t('sso.secret_kept') : t('sso.secret_hint')} error={shown('client_secret') ?? (!data.can_seal ? t('sso.cannot_seal') : null)}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              maxLength={500}
              placeholder={s.has_secret ? '••••••••••••' : ''}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t('sso.label')} hint={t('sso.label_hint', { label: label.trim() || t('sso.default_label') })}>
          <input className="input sso-label" maxLength={40} placeholder="Microsoft" value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <div className="sso-inline">
          <Field label={t('sso.callback')} hint={t('sso.callback_hint')}>
            <input className="input tabular" readOnly value={callback} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <Button icon="copy" onClick={() => copy()}>
            {t('sso.copy')}
          </Button>
        </div>
          </>
        )}
      </section>

      <section className="card" aria-labelledby="sso-who">
        <div className="card-head">
          <h2 id="sso-who">
            <span className="sso-step">2</span> {t('sso.who_title')}
          </h2>
        </div>
        <p className="muted">{t('sso.who_body')}</p>
        <div className="form-grid">
          <Field label={t('sso.domains')} hint={t('sso.domains_hint')} error={shown('domains')}>
            <input className="input" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="example.com" value={domains} onChange={(e) => setDomains(e.target.value)} />
          </Field>
          <Field label={t('sso.create')} hint={createRole ? t('sso.create_on_hint') : t('sso.create_off_hint')} error={shown('create_role')}>
            <select className="input" value={createRole} onChange={(e) => setCreateRole(e.target.value)}>
              <option value="">{t('sso.create_off')}</option>
              {data.roles.map((r) => (
                <option key={r.name} value={r.name}>
                  {t('sso.create_as', { role: roleLabel(r.name, data.roles as RoleInfo[]) })}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      <section className="card" aria-labelledby="sso-rules">
        <div className="card-head">
          <h2 id="sso-rules">
            <span className="sso-step">3</span> {t('sso.rules_title')}
          </h2>
        </div>
        <div className="list-row compact">
          <span className="list-main">
            <span className="list-title">{t('sso.trust_mfa')}</span>
            <span className="list-sub">{t('sso.trust_mfa_hint')}</span>
          </span>
          <Switch checked={trustMfa || ssoOnly} disabled={ssoOnly} label={t('sso.trust_mfa')} onChange={() => setTrustMfa((v) => !v)} />
        </div>
        <div className="list-row compact">
          <span className="list-main">
            <span className="list-title">{t('sso.only')}</span>
            <span className="list-sub">{t('sso.only_hint')}</span>
          </span>
          <Switch checked={ssoOnly} disabled={!enabled} label={t('sso.only')} onChange={() => setSsoOnly((v) => !v)} />
        </div>
        {ssoOnly && enabled ? (
          <p className="form-note warn" role="status">
            <Icon name="alert" size={16} /> {t('sso.only_warning', { n: data.people.total - data.people.with_email })}
          </p>
        ) : null}
      </section>

      <section className="card sso-save" aria-label={t('sso.save_area')}>
        <div className="list-row compact">
          <span className="list-main">
            <span className="list-title">{t('sso.enabled')}</span>
            <span className="list-sub">{enabled ? t('sso.enabled_on') : t('sso.enabled_off')}</span>
          </span>
          <Switch checked={enabled} label={t('sso.enabled')} onChange={() => setEnabled((v) => !v)} />
        </div>
        <div className="sso-actions">
          <Button variant="primary" icon="check" busy={busy} disabled={!me?.mfa} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </div>
      </section>
    </div>
  );
}
